import React, { useContext } from "react";
import useSpacing from '../../core/hooks/useSpacing';
import useBaseStyle from '../../core/hooks/useBaseStyle';
import useContainerStyle from '../../core/hooks/useContainerStyle';
import { EditContext } from "../../editor/context/EditContext";
import { getBackendUrl } from "../../core/utils/api";
import {
  isYouTubeUrl,
  getYouTubeVideoId,
  getPositionStyles,
  calculateWidth,
  calculateHeight,
} from "../utils/mediaUtils";

const VideoItem = ({ item }) => {
  const { combinedClasses } = useSpacing(item);
  const { styleClasses, inlineStyles } = useBaseStyle(item);
  const containerClasses = useContainerStyle(item);
  const editContext = useContext(EditContext);
  const isEditMode = !!editContext;

  // Handle null/undefined items gracefully
  if (!item) {
    return null;
  }
  // If no video URL, show a placeholder that"s clickable in edit mode
  if (!item.video_url || (typeof item.video_url === "string" && item.video_url.trim() === "")) {
    const finalClasses = `${combinedClasses} ${styleClasses} ${containerClasses}`.trim();

    return (
      <div
        className={`${finalClasses} min-h-[120px] border-2 border-dashed border-gray-400 bg-gray-100 hover:bg-gray-200 hover:border-gray-500 transition-all duration-200 flex flex-col items-center justify-center text-gray-600 rounded-lg cursor-pointer`}
        style={{
          ...inlineStyles,
          minHeight: "120px",
          minWidth: "200px",
        }}
        title="Click to add a video"
      >
        <div className="text-center p-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            className="w-10 h-10 mx-auto mb-2 text-gray-500 block"
          >
            <rect x="3" y="5" width="18" height="14" rx="2" ry="2" />
            <polygon points="10 9 15 12 10 15 10 9" />
          </svg>
          <div className="text-sm font-medium">No Video Selected</div>
          <div className="text-xs mt-1 opacity-75">Click to add a video</div>
        </div>
      </div>
    );
  }

  const finalClasses =
    `${combinedClasses} ${styleClasses} ${containerClasses}`.trim();
  const positionStyles = getPositionStyles(item.position);

  const getVideoStyle = () => {
    const videoStyle = {
      width: "100%",
      maxWidth: "100%",
      height: "auto",
      aspectRatio: "16/9",
    };

    const finalWidth = calculateWidth(item);
    if (finalWidth != null) {
      if (
        item.max_width_pct_component != null &&
        finalWidth === item.max_width_pct_component
      ) {
        videoStyle.width = `${finalWidth}%`;
      } else {
        videoStyle.width = `${finalWidth}px`;
      }
    }

    const finalHeight = calculateHeight(item);
    if (finalHeight != null) {
      videoStyle.height = finalHeight;
    }

    return videoStyle;
  };

  const renderYouTubeVideo = (videoId) => {
    const wrapperStyle = {
      width: item.max_width_pct_component
        ? `${item.max_width_pct_component}%`
        : "100%",
      maxWidth: item.max_width ? `${item.max_width}px` : "none",
      margin: "0 auto",
      position: "relative",
    };

    const iframeStyle = {
      width: "100%",
      height: "100%",
      aspectRatio: "16/9",
      border: "none",
    };

    return (
      <div style={wrapperStyle}>
        <iframe
          style={iframeStyle}
          src={`https://www.youtube.com/embed/${videoId}`}
          title="YouTube video player"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className={finalClasses}
        />
        {/* In edit mode, overlay captures clicks so EditableElement can select this element.
            Without this, iframe swallows all pointer events. */}
        {isEditMode && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              cursor: "pointer",
            }}
          />
        )}
      </div>
    );
  };

  const renderRegularVideo = () => {
    const videoStyle = getVideoStyle();
    const videoUrl = item.video_url.startsWith("http")
      ? item.video_url
      : `${getBackendUrl()}${item.video_url}`;

    return (
      <div style={{ position: "relative" }}>
        <video
          controls
          style={{ ...videoStyle, ...inlineStyles }}
          className={finalClasses}
        >
          <source src={videoUrl} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        {/* In edit mode, overlay captures clicks so EditableElement can select this element.
            Without this, the native video controls swallow click events. */}
        {isEditMode && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 1,
              cursor: "pointer",
            }}
          />
        )}
      </div>
    );
  };

  if (isYouTubeUrl(item.video_url)) {
    const videoId = getYouTubeVideoId(item.video_url);
    if (videoId) {
      return renderYouTubeVideo(videoId);
    }
  }

  return renderRegularVideo();
};

export default VideoItem;
